/**
 * Twitter/X counts every URL as exactly 23 characters (t.co shortening)
 * regardless of the actual URL length.
 */
const TWITTER_URL_LENGTH = 23;
const TWEET_MAX_LENGTH = 280;

/**
 * Finds the last complete sentence that fits within `maxLength`.
 * A sentence ends with `.` / `!` / `?` optionally followed by `"` / `)`.
 * Returns empty string if no full sentence fits.
 */
function truncateAtSentence({
  text,
  maxLength,
}: {
  text: string;
  maxLength: number;
}): string {
  if (text.length <= maxLength) {
    return text;
  }

  const window = text.slice(0, maxLength);
  // Match sentence-ending punctuation (optionally followed by closing quote/paren)
  const sentenceEnd = /[.!?][")\u201D]?\s/g;
  let lastMatch: RegExpExecArray | null = null;
  let match: RegExpExecArray | null = sentenceEnd.exec(window);
  while (match) {
    lastMatch = match;
    match = sentenceEnd.exec(window);
  }

  if (lastMatch) {
    // Include the punctuation, trim trailing whitespace
    return window.slice(0, lastMatch.index + lastMatch[0].trimEnd().length);
  }
  return "";
}

/**
 * Builds a pre-filled tweet that fits within 280 chars.
 *
 * Format:
 *   {headline}
 *
 *   {deck – trimmed to last full sentence, or omitted entirely}
 *
 *   via @aidailyprimer
 *   {url}
 *
 * Deck is truncated at **sentence boundaries** so the preview always
 * reads as a complete thought. If no full sentence fits the remaining
 * budget, the deck is dropped rather than showing an ugly fragment.
 */
export function buildShareText({
  headline,
  deck,
  storyUrl,
}: {
  headline: string;
  deck: string;
  storyUrl: string;
}): string {
  const attribution = "via @aidailyprimer";
  const separator = "\n\n";
  const suffix = `${attribution}\n${storyUrl}`;

  // Fixed overhead: separator + attribution line + t.co URL
  const suffixEffectiveLength =
    separator.length + attribution.length + "\n".length + TWITTER_URL_LENGTH;

  // Cap headline so it always fits with attribution + URL
  const maxHeadlineLength = TWEET_MAX_LENGTH - suffixEffectiveLength;
  const trimmedHeadline =
    headline.length > maxHeadlineLength
      ? `${headline.slice(0, maxHeadlineLength - 1)}…`
      : headline;

  const budgetForDeck =
    TWEET_MAX_LENGTH -
    trimmedHeadline.length -
    suffixEffectiveLength -
    separator.length;

  let trimmedDeck = "";
  if (budgetForDeck > 0 && deck.length > 0) {
    trimmedDeck = truncateAtSentence({ text: deck, maxLength: budgetForDeck });
  }

  const textParts = [trimmedHeadline, trimmedDeck, suffix].filter(Boolean);
  return textParts.join(separator);
}

/**
 * Returns a full `https://twitter.com/intent/tweet` URL with pre-filled text.
 */
export function buildShareOnXUrl({
  headline,
  deck,
  storyUrl,
}: {
  headline: string;
  deck: string;
  storyUrl: string;
}): string {
  const text = buildShareText({ headline, deck, storyUrl });
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
}
